import { useState } from "react";
import { useLocation } from "wouter";
import { ColumnDef, ColumnFiltersState, RowSelectionState } from "@tanstack/react-table";
import { Plus, Search, Eye, Trash2, Copy, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import DataTable from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
	useMappingProfiles,
	useDeleteMappingProfile,
	useCloneMappingProfile,
} from "@/hooks/useMappingProfiles";
import type { MappingProfile } from "@/types/mappingProfile";
import { format } from "date-fns";

const templateTypeLabels: Record<string, string> = {
	invoice: "Invoice",
	invoice_note: "CN/DN/RN",
	self_billed_invoice: "Self-Billed Invoice",
	self_billed_invoice_note: "Self-Billed CN/DN/RN",
};

const MappingProfilesListPage = () => {
	const [, navigate] = useLocation();
	const [search, setSearch] = useState("");
	const [templateType, setTemplateType] = useState<string>("");
	const [page, setPage] = useState(1);
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

	const { profiles, pagination, isLoading } = useMappingProfiles({
		templateType: templateType || undefined,
		page,
		limit: 20,
		search: search || undefined,
	});

	const { deleteProfile } = useDeleteMappingProfile();
	const { cloneProfile, isCloning } = useCloneMappingProfile();

	const selectedIds = Object.keys(rowSelection).filter(
		(id) => rowSelection[id],
	);
	const selectedProfiles = (profiles ?? []).filter((p) =>
		selectedIds.includes(p.id),
	);

	const handleCloneSelected = async () => {
		for (const p of selectedProfiles) {
			await cloneProfile({ id: p.id, name: `${p.name} (Copy)` });
		}
		setRowSelection({});
	};

	const handleDeleteSelected = async () => {
		for (const id of selectedIds) {
			await deleteProfile(id);
		}
		setRowSelection({});
	};

	const columns: ColumnDef<MappingProfile>[] = [
		{
			id: "select",
			header: ({ table }) => (
				<Checkbox
					checked={
						table.getIsAllPageRowsSelected() ||
						(table.getIsSomePageRowsSelected() && "indeterminate")
					}
					onCheckedChange={(value) =>
						table.toggleAllPageRowsSelected(!!value)
					}
					aria-label="Select all"
				/>
			),
			cell: ({ row }) => (
				<Checkbox
					checked={row.getIsSelected()}
					onCheckedChange={(value) => row.toggleSelected(!!value)}
					aria-label="Select row"
				/>
			),
			size: 40,
		},
		{
			id: "rowNumber",
			header: "#",
			cell: ({ row }) => (page - 1) * 20 + row.index + 1,
			size: 50,
		},
		{
			accessorKey: "name",
			header: "Profile Name",
			cell: ({ row }) => (
				<div>
					<div className="font-medium">{row.original.name}</div>
					{row.original.description && (
						<div className="text-xs text-muted-foreground truncate max-w-[200px]">
							{row.original.description}
						</div>
					)}
				</div>
			),
		},
		{
			accessorKey: "vendorName",
			header: "Vendor",
			cell: ({ row }) => row.original.vendorName || "-",
		},
		{
			accessorKey: "targetTemplateType",
			header: "Template",
			cell: ({ row }) =>
				templateTypeLabels[row.original.targetTemplateType] ||
				row.original.targetTemplateType,
		},
		{
			id: "aiGenerated",
			header: "Source",
			cell: ({ row }) =>
				row.original.aiGenerated ? (
					<Badge variant="secondary">AI-Generated</Badge>
				) : (
					<Badge variant="outline">Manual</Badge>
				),
		},
		{
			id: "status",
			header: "Status",
			cell: ({ row }) =>
				row.original.isActive ? (
					<Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
						Active
					</Badge>
				) : (
					<Badge variant="secondary">Inactive</Badge>
				),
		},
		{
			accessorKey: "createdAt",
			header: "Created",
			cell: ({ row }) =>
				format(new Date(row.original.createdAt), "dd MMM yyyy"),
		},
		{
			id: "actions",
			header: "Actions",
			cell: ({ row }) => (
				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="icon"
						onClick={() =>
							navigate(`/mapping-profiles/${row.original.id}`)
						}
					>
						<Eye className="h-4 w-4" />
					</Button>
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button variant="ghost" size="icon">
								<Trash2 className="h-4 w-4 text-destructive" />
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									Delete Profile
								</AlertDialogTitle>
								<AlertDialogDescription>
									Are you sure you want to delete "
									{row.original.name}"? This action can be
									undone by an admin.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction
									onClick={() =>
										deleteProfile(row.original.id)
									}
								>
									Delete
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			),
		},
	];

	return (
		<div className="space-y-4">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">
						Mapping Profiles
					</h1>
					<p className="text-muted-foreground">
						Configure column mappings to convert vendor Excel files
						into QuickBill import format.
					</p>
				</div>
				<Button
					onClick={() => navigate("/mapping-profiles/create")}
				>
					<Plus className="mr-2 h-4 w-4" />
					New Profile
				</Button>
			</div>

			{/* Filters */}
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
				<div className="relative flex-1 max-w-sm">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder="Search profiles..."
						className="pl-8"
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(1);
						}}
					/>
				</div>
				<Select
					value={templateType}
					onValueChange={(val) => {
						setTemplateType(val === "all" ? "" : val);
						setPage(1);
					}}
				>
					<SelectTrigger className="w-[200px]">
						<SelectValue placeholder="All Templates" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All Templates</SelectItem>
						<SelectItem value="invoice">Invoice</SelectItem>
						<SelectItem value="invoice_note">CN/DN/RN</SelectItem>
						<SelectItem value="self_billed_invoice">
							Self-Billed Invoice
						</SelectItem>
						<SelectItem value="self_billed_invoice_note">
							Self-Billed CN/DN/RN
						</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{/* Bulk actions */}
			{selectedIds.length > 0 && (
				<div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
					<span className="text-sm text-muted-foreground">
						{selectedIds.length} selected
					</span>
					<Button
						variant="secondary"
						size="sm"
						disabled={isCloning}
						onClick={handleCloneSelected}
					>
						{isCloning ? (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						) : (
							<Copy className="mr-2 h-4 w-4" />
						)}
						Clone Profile
					</Button>
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button variant="destructive" size="sm">
								<Trash2 className="mr-2 h-4 w-4" />
								Delete Profile
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									Delete {selectedIds.length} Profile(s)
								</AlertDialogTitle>
								<AlertDialogDescription>
									Are you sure you want to delete the selected
									profile(s)? This action can be undone by an
									admin.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction onClick={handleDeleteSelected}>
									Delete
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			)}

			{/* Table */}
			{isLoading ? (
				<div className="flex items-center justify-center h-64">
					<div className="text-muted-foreground">Loading...</div>
				</div>
			) : (
				<>
					<DataTable
						columns={columns}
						data={profiles ?? []}
						rowSelection={rowSelection}
						setRowSelection={setRowSelection}
						columnFilters={columnFilters}
						setColumnFilters={setColumnFilters}
					/>

					{/* Pagination */}
					{pagination && pagination.totalPages > 1 && (
						<div className="flex items-center justify-between">
							<div className="text-sm text-muted-foreground">
								Showing {(page - 1) * 20 + 1} to{" "}
								{Math.min(page * 20, pagination.total)} of{" "}
								{pagination.total} profiles
							</div>
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									disabled={page <= 1}
									onClick={() => setPage(page - 1)}
								>
									Previous
								</Button>
								<span className="text-sm">
									Page {page} of {pagination.totalPages}
								</span>
								<Button
									variant="outline"
									size="sm"
									disabled={page >= pagination.totalPages}
									onClick={() => setPage(page + 1)}
								>
									Next
								</Button>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default MappingProfilesListPage;
